import { sendHttpRequest, axiosInstance } from "lib/fetcher";
import { useContext, useRef, useEffect, useState } from "react";
import { SignalContext } from "context/SignalContext";
import { DB_STORE, googleSSOClientId } from "lib/constants";
import Cookies from "js-cookie";
import { gapi } from "gapi-script";

export const CHUNK_SIZE = 1 * 1024 * 512; // never change this value
export default function useUploadToDriveService(open) {
  const { getDb } = useContext(SignalContext);
  const googleTokenRef = useRef("");
  const openRef = useRef();
  const [tokenWillExpire, setTokenWillExpire] = useState(true);
  const tokenWillExpireRef = useRef(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  async function chunkToDb(caseId, subId, chunk, data) {
    const db = await getDb();
    await db.put(DB_STORE.GOOGLE_DRIVE_CACHE, {
      recordId: subId,
      chunk: chunk,
      data: data,
      id: `${caseId}_${subId}_${chunk}`,
    });
  }

  async function getOneChunkFromDb(caseId, subId, chunk) {
    try {
      const db = await getDb();
      const data = await db.read(
        DB_STORE.GOOGLE_DRIVE_CACHE,
        `${caseId}_${subId}_${chunk}`,
      );
      return data;
    } catch (error) {
      console.error("Worker error:", error);
    }
  }

  async function fetchS3File(
    caseId: string,
    subId: string,
    url: string,
    startByte: number = 0,
    onDownloadProgress?: (progress: number, total: number) => void,
  ) {
    const db = await getDb();
    if (startByte === 0) {
      db.db?.deleteMatchData(DB_STORE.GOOGLE_DRIVE_CACHE, "recordId", subId);
    }

    const headers = startByte > 0 ? { Range: `bytes=${startByte}-` } : {};
    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const reader = response.body.getReader();

    let chunkIndex = Math.floor(startByte / CHUNK_SIZE);
    let receivedLength = startByte % CHUNK_SIZE; // 已接收的字节长度
    const totalSize =
      Number(response.headers.get("Content-Length")) + startByte;
    console.log("totalSize", totalSize);

    const chunks = [];
    // eslint-disable-next-line no-constant-condition
    while (openRef.current) {
      let done, value;
      try {
        ({ done, value } = await reader.read());
      } catch (error) {
        console.error("fetchS3File error:", error);
        throw new Error("Network error occurred during read");
      }

      if (done) {
        break;
      }

      chunks.push(value);
      receivedLength += value.byteLength;

      // 如果达到或超过固定大小，则存储分片
      while (receivedLength >= CHUNK_SIZE) {
        const chunk = new Uint8Array(CHUNK_SIZE);
        let offset = 0;

        while (offset < CHUNK_SIZE) {
          // console.log("chunks", chunks);
          if (chunks.length === 0) {
            break;
          }
          const currentChunk = chunks[0];
          const remainingSpace = CHUNK_SIZE - offset;

          if (currentChunk.byteLength <= remainingSpace) {
            chunk.set(currentChunk, offset);
            offset += currentChunk.byteLength;
            chunks.shift();
          } else {
            chunk.set(currentChunk.subarray(0, remainingSpace), offset);
            chunks[0] = currentChunk.subarray(remainingSpace);
            offset += remainingSpace;
          }
        }

        // 存储分片到 IndexedDB
        await chunkToDb(caseId, subId, chunkIndex++, chunk);
        onDownloadProgress?.(chunkIndex * CHUNK_SIZE, totalSize);
        console.log("lzc totalReceived", chunkIndex * CHUNK_SIZE);
        receivedLength -= CHUNK_SIZE;
      }
    }

    // 存储最后一个分片（如果有剩余数据）
    if (receivedLength > 0) {
      const chunk = new Uint8Array(receivedLength);
      let offset = 0;

      for (let i = 0; i < chunks.length; i++) {
        chunk.set(chunks[i], offset);
        offset += chunks[i].byteLength;
      }

      await chunkToDb(caseId, subId, chunkIndex++, chunk);
    }
    return totalSize;
  }

  async function searchFolderProperty(
    accessToken: string,
    propertyKey: string,
    propertyValue: string,
  ) {
    const config = {
      method: "get",
      url: `https://www.googleapis.com/drive/v3/files?q=mimeType='application/vnd.google-apps.folder' and trashed=false and properties has { key='${propertyKey}' and value='${propertyValue}' }`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    };

    return await axiosInstance(config);
  }

  async function deleteFile(accessToken: string, fileId: string) {
    const config = {
      method: "delete",
      url: `https://www.googleapis.com/drive/v3/files/${fileId}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    };
    return await axiosInstance(config);
  }

  async function startUpload(
    accessToken: string,
    metadata: {
      name: string;
      mimeType: string;
      parents: string[];
    },
  ) {
    const config = {
      method: "post",
      url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=resumable",
      data: metadata,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    };
    return await axiosInstance(config);
  }

  const createFolder = async (
    accessToken: string,
    folderName: string,
    parentFolderId?: string,
    propertyKey?: string,
    propertyValue?: string,
  ) => {
    const folderMetadata = {
      name: folderName,
      mimeType: "application/vnd.google-apps.folder",
      properties: {},
    };
    parentFolderId &&
      Object.assign(folderMetadata, { parents: [parentFolderId] });
    propertyKey && (folderMetadata.properties[propertyKey] = propertyValue);

    const config = {
      method: "post",
      url: `https://www.googleapis.com/drive/v3/files?`,
      data: folderMetadata,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    };

    return await axiosInstance(config);
  };

  async function doUploadChunk(
    accessToken: string,
    url,
    chunk,
    start,
    end,
    totalSize,
  ) {
    const config = {
      method: "put",
      url: url,
      data: chunk,
      headers: {
        "Content-Type": chunk.type,
        "Content-Range": `bytes ${start}-${end}/${totalSize}`,
      },
    };
    return await sendHttpRequest(config, accessToken);
  }

  async function getGoogleToken(): Promise<string> {
    return new Promise((resolve) => {
      const timeId = setInterval(() => {
        checkGoogleToken();
      }, 1000);
      const checkGoogleToken = () => {
        if (!tokenWillExpireRef.current && googleTokenRef.current) {
          clearInterval(timeId);
          resolve(googleTokenRef.current);
        }
      };

      checkGoogleToken();
    });
  }

  useEffect(() => {
    if (!open) {
      return;
    }
    const timeId = setInterval(() => {
      const expiresAt = Cookies.get("googleTokenExpiresAt");
      if (expiresAt && parseInt(expiresAt) > Date.now() + 1000 * 60 * 5) {
        setTokenWillExpire(false);
        tokenWillExpireRef.current = false;
      } else {
        setTokenWillExpire(true);
        tokenWillExpireRef.current = true;
      }
    }, 1000);

    return () => {
      clearInterval(timeId);
    };
  }, [open]);

  useEffect(() => {
    const redirect_uri = encodeURIComponent(window.location?.origin + "/sso");
    const start = () => {
      gapi.client.init({
        clientId: googleSSOClientId,
        scope: "email profile https://www.googleapis.com/auth/drive",
        prompt: "consent",
        access_type: "offline",
        redirect_uri: redirect_uri, // 指定重定向地址
      });
    };

    gapi.load("client:auth2", start);

    const googleEmail = Cookies.get("googleEmail");
    setEmail(googleEmail);
    const googleToken = Cookies.get("googleToken");
    googleTokenRef.current = googleToken;
  }, []);

  const loginGoogle = async () => {
    try {
      const loginRes = await gapi.auth2.getAuthInstance().signIn({
        prompt: "consent",
        access_type: "offline",
        scope: "email profile https://www.googleapis.com/auth/drive",
      });
      const profile = loginRes.getBasicProfile();
      const email = profile.getEmail();

      const accessToken = loginRes.getAuthResponse().access_token;
      const expiresAt = loginRes.getAuthResponse().expires_at;
      googleTokenRef.current = accessToken;
      setEmail(email);

      Cookies.set("googleToken", accessToken);

      Cookies.set("googleEmail", email);
      Cookies.set("googleTokenExpiresAt", expiresAt);
      //const refreshToken = res.getAuthResponse().refresh_token; // 仅在请求 `offline` 权限时返回
      console.log("getAuthResponse:", loginRes.getAuthResponse());
    } catch (error) {
      console.error("loginGoogle error:", error);
    }
  };

  return {
    email,
    tokenWillExpire,
    loginGoogle,
    getGoogleToken,
    searchFolderProperty,
    deleteFile,
    startUpload,
    doUploadChunk,
    createFolder,
    fetchS3File,
    getOneChunkFromDb,
  };
}
